import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../state/auth';
import { Icon } from './Icon';
import { Avatar } from './ui';

export interface Crumb {
  label: string;
  to?: string;
}

export function Brand() {
  return (
    <Link to="/" className="brand" aria-label="Revisions by MK-Way, home">
      <span className="brand-mark" aria-hidden="true">
        R
      </span>
      <span className="brand-word">Revisions</span>
      <span className="brand-by" aria-hidden="true">
        <span className="brand-by-text">by</span>
        <img className="brand-logo" src="/mk-way-logo.png" alt="" width="288" height="154" />
      </span>
    </Link>
  );
}

function UserMenu() {
  const { user, isStaff, isAdmin, scopeProjectId, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className="menu-wrap" ref={wrap}>
      <button type="button" className="user-chip" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <Avatar name={user.name} kind={user.role === 'client' ? 'client' : 'team'} />
        <span className="user-chip-name truncate">{user.name}</span>
        <Icon name="chevron-down" />
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-label truncate">
            {user.email}
            {scopeProjectId ? ' · guest' : ''}
          </div>
          {user.hasPassword && (
            <button className="menu-item" role="menuitem" onClick={() => go('/account')}>
              <Icon name="user" /> Account
            </button>
          )}
          {isAdmin && (
            <button className="menu-item" role="menuitem" onClick={() => go('/settings/users')}>
              <Icon name="users" /> People
            </button>
          )}
          {isStaff && (
            <button className="menu-item" role="menuitem" onClick={() => go('/settings/api-keys')}>
              <Icon name="key" /> API keys
            </button>
          )}
          {isAdmin && (
            <button className="menu-item" role="menuitem" onClick={() => go('/settings/debug')}>
              <Icon name="bug" /> Debug
            </button>
          )}
          <button
            className="menu-item"
            role="menuitem"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            <Icon name="logout" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function AppHeader({ crumbs = [], actions }: { crumbs?: Crumb[]; actions?: ReactNode }) {
  return (
    <header className="app-header">
      <div className="row grow" style={{ gap: 16 }}>
        <Brand />
        {crumbs.length > 0 && (
          <nav className="crumbs" aria-label="Breadcrumb">
            {crumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`} className="row" style={{ gap: 8, minWidth: 0 }}>
                <Icon name="chevron-right" className="faint" />
                {crumb.to ? (
                  <Link to={crumb.to} className="truncate">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="truncate crumb-current" aria-current="page">
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </nav>
        )}
      </div>
      <div className="row" style={{ gap: 12 }}>
        {actions}
        <UserMenu />
      </div>
    </header>
  );
}

export function AppShell({ crumbs, actions, children, width = 'normal' }: { crumbs?: Crumb[]; actions?: ReactNode; children: ReactNode; width?: 'normal' | 'narrow' }) {
  return (
    <div className="app">
      <AppHeader crumbs={crumbs} actions={actions} />
      <main className="app-main">
        <div className={`page ${width === 'narrow' ? 'page-narrow' : ''}`}>{children}</div>
      </main>
    </div>
  );
}
